import confetti, { Options } from 'canvas-confetti';
import { Trans, useTranslation } from 'react-i18next';
import { LogoLoreal, LogoPuma, LogoSamsung } from '@app/modules/auth/assets';
import { AttachIcon, BrowserIcon, CheckCircleIcon, FiRrInfo, PrinterIcon, ScanIcon, ShareIcon } from '@app/assets';
import OnboardingQRDecorationSrc from '../../../assets/onboarding-qr-decoration.png';
import OnboardingRewievIPhoneSrc from '../../../assets/onboarding-rewiev-iPhone.png';
import OnboardPhoneBGSrc from '../../../assets/onboarding-phones-bg.png';
import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { QRCodeRendererSVG, QRRendererProps } from '@app/domain/qr-code/components/QRCodeRendererSVG';
import { ContentLoader } from '@app/components/loaders/content-loader/ContentLoader';
import { Button } from '@app/components';
import { ETrialOnboardingType } from '../TrialOnboardingDialog';
import { useActivePlan } from '@app/domain/plan/hooks/useActivePlan';
import { EOnboardingModalType } from '@app/domain/plan/hooks/useTrialPhasesConfig';
import { PermissionCrown } from '@app/domain/plan/components/permission-crown/PermissionCrown';
import { QRCodeOutDtoOverridden } from '@app/domain/qr-code/qr-code.types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { clsxm } from '@app/styles/clsxm';
import { TRIAL_ONBOARDING_ARROW_END_ELEMENT_ID, TRIAL_ONBOARDING_ARROW_START_ELEMENT_ID } from '../onboarding.constant';
import { TrialOnboardingScanPolling } from './components/TrialOnboardingScanPolling';
import { useIsMobile } from '@app/hooks/useIsMobile.hook';

export const useTrialOnboardingDialogContent = (
  trialOnboarding: ETrialOnboardingType | null,
  modalType: EOnboardingModalType,
  options: { qrCode?: QRCodeOutDtoOverridden | null; isLoading?: boolean; rendererProps?: QRRendererProps }
): {
  header: JSX.Element;
  description: JSX.Element;
  decorativeContent: JSX.Element;
  shouldShowAnimatedArrow: boolean;
} => {
  const { t } = useTranslation();
  const { currentUser } = useAuthorizedUser();
  const { daysToExpire, isExperimentGroupB, isExperimentGroupC } = useActivePlan();
  const isMobile = useIsMobile();

  const { qrCode, isLoading, rendererProps: qrRendererProps } = options;

  let header = <>{t('limit.dialog.common.upgradeToUnlock')}</>;
  let description = <></>;
  let decorativeContent = <></>;
  let shouldShowAnimatedArrow = false;

  const decorativeContentComponent = (
    <div className="top-0 left-0 md:pt-16 md:p-8 md:absolute w-full h-full bg-gray-100 flex flex-col justify-between">
      <div className="hidden md:flex flex-col gap-4">
        <div className="font-medium text-xs text-gray-700">{t('limit.dialog.common.testimonial')}</div>
        <div className="flex flex-col gap-1">
          <div className="text-xs italic text-gray-700 font-semibold">{t('limit.dialog.common.author')}</div>
          <div className="text-gray-500 text-xs italic">{t('limit.dialog.common.authorPosition')}</div>
        </div>
      </div>
      <div className="flex flex-col gap-4 mb-0 py-4 md:py-0 items-center md:items-stretch">
        <div className="text-xs font-medium text-gray-700">{t('onboarding.stepSelectCycle.rightSide.trustedBy')}</div>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white p-2 rounded-lg text-black flex items-center justify-center h-9 md:h-auto">
            <LogoLoreal className="w-16" />
          </div>
          <div className="bg-white p-2 rounded-lg text-black flex items-center justify-center h-9 md:h-auto">
            <LogoPuma className="w-16 scale-75 md:scale-100" />
          </div>
          <div className="bg-white p-2 rounded-lg text-black flex items-center justify-center h-9 md:h-auto">
            <LogoSamsung className="w-16" />
          </div>
        </div>
      </div>
    </div>
  );

  const getTrialOnboardingContentByType = () => {
    switch (modalType) {
      case EOnboardingModalType.MODAL_2A:
        header = (
          <div className="flex flex-col gap-4">
            <div className="text-gray-800 text-xl font-bold">{t('limit.dialog.modal_2a.title')}</div>
            <div className="font-normal text-xs text-gray-700">{t('limit.dialog.modal_2a.description')}</div>
          </div>
        );
        description = (
          <div className="flex flex-col gap-4 mt-4 mb-auto">
            <div className="font-normal text-xs text-gray-700">
              <Trans i18nKey={'limit.dialog.modal_2a.message'}>
                <b />
              </Trans>
            </div>
          </div>
        );
        decorativeContent = (
          <>
            <img
              src={OnboardingQRDecorationSrc}
              alt="Create first QR code prewiev"
              className="w-full h-full object-cover absolute inset-0"
            />
          </>
        );
        break;
      case EOnboardingModalType.MODAL_2B:
        header = (
          <div className="flex flex-col gap-4">
            <div className="text-gray-800 text-xl font-bold">{t('limit.dialog.modal_2b.title')}</div>
            <div className="font-normal text-xs text-gray-700">{t('limit.dialog.modal_2b.description')}</div>
          </div>
        );
        description = (
          <div className="flex flex-col gap-4 mt-4 mb-auto">
            <div className="font-normal text-xs text-gray-700" id={TRIAL_ONBOARDING_ARROW_START_ELEMENT_ID}>
              <Trans i18nKey={'limit.dialog.modal_2b.message'}>
                <b />
              </Trans>
            </div>
          </div>
        );
        shouldShowAnimatedArrow = true;
        decorativeContent = (
          <div className="before:vontent-[''] block p-8 bg-gray-100 md:bg-transparent m-4 mt-0 rounded-2xl h-72 before:inset-0 before:absolute before:bg-gray-100">
            {qrRendererProps && qrCode && !isLoading ? (
              <div className="flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 justify-center items-center w-44 h-44 md:w-52 md:h-52 rounded-2xl shadow-xl bg-white">
                <div className="w-28 md:w-36" id={TRIAL_ONBOARDING_ARROW_END_ELEMENT_ID}>
                  <QRCodeRendererSVG {...qrRendererProps} />
                </div>
                <TrialOnboardingScanPolling />
              </div>
            ) : (
              <div
                id={TRIAL_ONBOARDING_ARROW_END_ELEMENT_ID}
                className="absolute flex items-center top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] w-36 h-36"
              >
                <ContentLoader isEmpty={false} loading entity="QR Codes" />
              </div>
            )}
          </div>
        );
        break;
      case EOnboardingModalType.MODAL_HIGH_INTENT_USER:
        header = (
          <div className="flex flex-col gap-4">
            <div className="text-grey-700 inline-flex gap-2 font-semibold text-xs">
              <PermissionCrown className="shrink-0" /> {t('limit.dialog.highIntentUser.upgrageMessage')}
            </div>
            <div className="text-gray-800 text-xl font-bold">{t('limit.dialog.highIntentUser.title')}</div>
            <div className="font-normal text-xs text-gray-700">
              <Trans
                i18nKey={'limit.dialog.highIntentUser.description'}
                values={{
                  totalQRCodes: currentUser?.totalCodes || 0,
                }}
              >
                <b />
              </Trans>
            </div>
          </div>
        );
        description = (
          <div className="flex flex-col gap-4 mt-4 mb-auto">
            <div className="font-normal text-xs text-gray-700">
              <Trans i18nKey={'limit.dialog.highIntentUser.message'}>
                <b />
              </Trans>
            </div>
            <div className="font-normal text-xs text-gray-700">{t('limit.dialog.highIntentUser.warningTilte')}</div>
            <div className="flex gap-3 items-start rounded-lg bg-gray-200 border border-gray-300 px-3 py-2">
              <FiRrInfo className="shrink-0 text-error" />
              <div className="flex flex-col gap-3">
                <div className="font-normal text-xs text-gray-700">
                  <Trans
                    i18nKey={'limit.dialog.highIntentUser.warning'}
                    values={{
                      daysToExpire,
                    }}
                  >
                    <b />
                  </Trans>
                </div>
              </div>
            </div>
          </div>
        );
        decorativeContent = decorativeContentComponent;
        break;
      case EOnboardingModalType.MODAL_TRIAL_EXPIRED:
        header = (
          <div className="flex flex-col gap-4">
            <div className="text-grey-700 inline-flex gap-2 font-semibold text-xs">
              <PermissionCrown className="shrink-0" /> {t('limit.dialog.trialExpired.upgrageMessage')}
            </div>
            <div className="text-gray-800 text-xl font-bold">{t('limit.dialog.trialExpired.title')}</div>
            <div className="font-normal text-xs text-gray-700">
              <Trans i18nKey={'limit.dialog.trialExpired.description'}>
                <b />
              </Trans>
            </div>
          </div>
        );
        description = (
          <div className="flex flex-col gap-4 mt-4 mb-auto">
            <div className="font-normal text-xs text-gray-700">{t('limit.dialog.trialExpired.message')}</div>
            {Boolean(currentUser?.totalCodes || currentUser?.totalScans) && (
              <div className="font-normal text-xs text-gray-700">{t('limit.dialog.trialExpired.info')}</div>
            )}

            <div className="flex gap-4">
              {Boolean(currentUser?.totalCodes) && (
                <div className="inline-flex gap-1 text-gray-700 text-xs font-semibold p-2 rounded-lg bg-gray-100">
                  <span>{currentUser?.totalCodes}</span>
                  <span>{t('subscription.qrCodes')}</span>
                </div>
              )}
              {Boolean(currentUser?.totalScans) && (
                <div className="inline-flex gap-1 text-gray-700 text-xs font-semibold p-2 rounded-lg bg-gray-100">
                  <span>{currentUser?.totalScans}</span>
                  <span>{t('subscription.scans')}</span>
                </div>
              )}
            </div>
          </div>
        );
        decorativeContent = decorativeContentComponent;
        break;
      case EOnboardingModalType.SCAN_CONGRATULATION:
        header = (
          <div className="flex flex-col gap-4">
            <div className="text-gray-800 text-xl font-bold">{t('limit.dialog.scanCongratulations.title')}</div>
            <div className="font-normal text-xs text-gray-700">
              <Trans
                i18nKey={
                  isExperimentGroupC
                    ? 'limit.dialog.scanCongratulations.descriptionC'
                    : 'limit.dialog.scanCongratulations.description'
                }
              >
                <b />
              </Trans>
            </div>
          </div>
        );
        description = <div className="flex mb-auto"></div>;
        decorativeContent = (
          <>
            {!isMobile && <Confetti run />}
            <div className="before:vontent-[''] block p-8 bg-gray-100 md:bg-transparent m-4 mt-0 rounded-2xl h-72 before:inset-0 before:absolute before:bg-gray-100">
              {qrRendererProps && qrCode && !isLoading ? (
                <div className="flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 justify-center items-center w-44 h-44 md:w-52 md:h-52 rounded-2xl shadow-xl bg-white">
                  <div className="w-28 md:w-36" id={TRIAL_ONBOARDING_ARROW_END_ELEMENT_ID}>
                    <QRCodeRendererSVG {...qrRendererProps} />
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="animate-scale-in">
                      <div className="bg-accept-medium rounded-full flex p-1 mx-auto">
                        <CheckCircleIcon className="-m-1 w-14 h-14 text-white bg-white rounded-full" />
                      </div>
                    </div>
                  </div>
                  <TrialOnboardingScanPolling />
                </div>
              ) : (
                <div
                  id={TRIAL_ONBOARDING_ARROW_END_ELEMENT_ID}
                  className="absolute flex items-center top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] w-36 h-36"
                >
                  <ContentLoader isEmpty={false} loading entity="QR Codes" />
                </div>
              )}
            </div>
          </>
        );
        break;
      case EOnboardingModalType.NONE:
      default:
        header = <>{t('limit.dialog.common.upgradeToUnlock')}</>;
        description = <></>;
        decorativeContent = <></>;
        break;
    }
  };

  switch (trialOnboarding) {
    case ETrialOnboardingType.TRIAL_EXTEND_TRIAL:
      header = (
        <div className="flex flex-col gap-4">
          <div className="text-gray-800 text-xl font-bold">{t('limit.dialog.extendTrial.title')}</div>
          <div className="font-normal text-xs text-gray-700">
            <Trans i18nKey={'limit.dialog.extendTrial.description'}>
              <b />
            </Trans>
          </div>

          <div className="font-normal text-xs">
            {isExperimentGroupB ? t('limit.dialog.extendTrial.messageB') : t('limit.dialog.extendTrial.message')}
          </div>
          <Button
            variant="outlined"
            onClick={() => {
              window.open(
                isExperimentGroupB
                  ? 'https://g.page/r/CblLZ4i8iL6YEAE/review'
                  : 'https://www.trustpilot.com/evaluate/trueqrcode.com',

                '_blank'
              );
            }}
          >
            {isExperimentGroupB
              ? t('limit.dialog.extendTrial.writeReviewB')
              : t('limit.dialog.extendTrial.writeReview')}
          </Button>
        </div>
      );
      description = <div className="flex mb-auto"></div>;
      decorativeContent = (
        <>
          <img src={OnboardPhoneBGSrc} alt="" className="w-full h-full object-cover absolute inset-0" />
          <div className="w-full h-full bg-black/60 absolute inset-0" />
          <svg
            className="hidden md:inline absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            width="332"
            height="368"
            viewBox="0 0 332 368"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M84 57v68.969a41 41 0 0 0 26.88 38.491l110.24 40.44A41 41 0 0 1 248 243.392V313"
              stroke="#fff"
              strokeDasharray="6 6"
            />
            <g clipPath="url(#clip0_114_3368)">
              <foreignObject x="-45.607" y="-45.607" width="259.215" height="139.215">
                <div
                  style={{
                    backdropFilter: 'blur(22.8px)',
                    clipPath: 'url(#bgblur_1_114_3368_clip_path)',
                    height: '100%',
                    width: '100%',
                  }}
                />
              </foreignObject>
              <rect
                data-figma-bg-blur-radius="45.607"
                x=".57"
                y=".57"
                width="166.86"
                height="46.86"
                rx="11.43"
                fill="#fff"
                fillOpacity=".4"
                stroke="url(#paint0_radial_114_3368)"
                strokeWidth="1.14"
              />
              <g clipPath="url(#clip2_114_3368)" fill="#F0CA00">
                <path d="M27.049 14.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 0 0 .95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 0 0-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 0 0-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 0 0-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 0 0 .951-.69zm28 0c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 0 0 .95.69h4.915c.969 0 1.372 1.24.588 1.81l-3.976 2.888a1 1 0 0 0-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 0 0-1.176 0l-3.975 2.888c-.784.57-1.839-.197-1.54-1.118l1.52-4.674a1 1 0 0 0-.364-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 0 0 .951-.69zm28 0c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 0 0 .95.69h4.915c.969 0 1.372 1.24.588 1.81l-3.976 2.888a1 1 0 0 0-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 0 0-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 0 0-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 0 0 .951-.69zm28 0c.299-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 0 0 .951.69h4.914c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 0 0-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 0 0-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 0 0-.363-1.118l-3.976-2.888c-.783-.57-.381-1.81.588-1.81h4.914a1 1 0 0 0 .951-.69zm28 0c.299-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 0 0 .951.69h4.914c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 0 0-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 0 0-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 0 0-.363-1.118l-3.976-2.888c-.783-.57-.381-1.81.588-1.81h4.914a1 1 0 0 0 .951-.69z" />
              </g>
            </g>
            <g clipPath="url(#clip3_114_3368)">
              <foreignObject x="118.393" y="274.393" width="259.215" height="139.215">
                <div
                  style={{
                    backdropFilter: 'blur(22.8px)',
                    clipPath: 'url(#bgblur_4_114_3368_clip_path)',
                    height: '100%',
                    width: '100%',
                  }}
                />
              </foreignObject>
              <rect
                x="164.57"
                y="320.57"
                width="166.86"
                height="46.86"
                rx="11.43"
                fill="#2C9424"
                stroke="url(#paint1_radial_114_3368)"
                strokeWidth="1.14"
              />
              <text x="180" y="350" fill="#fff" className="font-semibold select-none">
                Get a PRO for free
              </text>
            </g>
            <g filter="url(#filter2_d_114_3368)">
              <circle cx="84" cy="53" r="4" fill="#2C9424" />
              <circle cx="84" cy="53" r="4.5" stroke="#D4EED2" />
            </g>
            <g clipPath="url(#clip5_114_3368)">
              <path d="M260 317c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12" fill="#48B240" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M248 327.5c5.799 0 10.5-4.701 10.5-10.5s-4.701-10.5-10.5-10.5-10.5 4.701-10.5 10.5 4.701 10.5 10.5 10.5m0 1.5c6.627 0 12-5.373 12-12s-5.373-12-12-12-12 5.373-12 12 5.373 12 12 12"
                fill="#D4EED2"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M252.78 313.97a.75.75 0 0 1 0 1.06l-6.009 6.01-3.78-3.489a.75.75 0 0 1 1.018-1.102l2.72 2.511 4.991-4.99a.75.75 0 0 1 1.06 0"
                fill="#F6F7F8"
              />
            </g>
            <defs>
              <clipPath id="bgblur_1_114_3368_clip_path" transform="translate(45.607 45.607)">
                <rect x=".57" y=".57" width="166.86" height="46.86" rx="11.43" />
              </clipPath>
              <clipPath id="bgblur_4_114_3368_clip_path" transform="translate(-118.393 -274.393)">
                <rect x="164.57" y="320.57" width="166.86" height="46.86" rx="11.43" />
              </clipPath>
              <clipPath id="clip0_114_3368">
                <path fill="#fff" d="M0 0h168v48H0z" />
              </clipPath>
              <clipPath id="clip2_114_3368">
                <path fill="#fff" d="M16 12h136v24H16z" />
              </clipPath>
              <clipPath id="clip3_114_3368">
                <path fill="#fff" d="M164 320h168v48H164z" />
              </clipPath>
              <clipPath id="clip5_114_3368">
                <path fill="#fff" d="M236 305h24v24h-24z" />
              </clipPath>
              <radialGradient
                id="paint0_radial_114_3368"
                cx="0"
                cy="0"
                r="1"
                gradientUnits="userSpaceOnUse"
                gradientTransform="rotate(31.503 5.559 -41.173)scale(141.118 141.124)"
              >
                <stop stopColor="#fff" stopOpacity=".75" />
                <stop offset="1" stopColor="#fff" stopOpacity="0" />
              </radialGradient>
              <radialGradient
                id="paint1_radial_114_3368"
                cx="0"
                cy="0"
                r="1"
                gradientUnits="userSpaceOnUse"
                gradientTransform="rotate(31.503 -479.697 409.544)scale(141.118 141.124)"
              >
                <stop stopColor="#fff" stopOpacity=".75" />
                <stop offset="1" stopColor="#fff" stopOpacity="0" />
              </radialGradient>
              <filter
                id="filter2_d_114_3368"
                x="75"
                y="44"
                width="18"
                height="18"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix values="0 0 0 0 0.172549 0 0 0 0 0.580392 0 0 0 0 0.141176 0 0 0 0.5 0" />
                <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_114_3368" />
                <feBlend in="SourceGraphic" in2="effect1_dropShadow_114_3368" result="shape" />
              </filter>
            </defs>
          </svg>
        </>
      );
      break;
    case ETrialOnboardingType.TRIAL_THANKS_REVIEW:
      header = (
        <div className="flex flex-col gap-4">
          <div className="text-gray-800 text-xl font-bold">{t('limit.dialog.thanksReview.title')}</div>
          <div className="font-normal text-xs text-gray-700">{t('limit.dialog.thanksReview.description')}</div>
        </div>
      );
      description = <div className="flex mb-12 md:mb-auto"></div>;
      decorativeContent = (
        <div className="h-72 static w-auto md:h-full object-cover md:absolute inset-0 m-4 md:m-0 bg-gradient-to-b from-white to-accept-hover rounded-2xl md:rounded-none">
          <div className="flex justify-center relative w-44 h-44 md:w-auto md:h-96 overflow-hidden md:overflow-visible top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl">
            <img
              src={OnboardingRewievIPhoneSrc}
              alt="iPhone with rewiev star"
              className="md:w-auto md:h-96 scale-[3] md:scale-100 drop-shadow-xl mt-7 md:mt-0 object-cover md:object-contain"
            />
          </div>
        </div>
      );
      break;
    case ETrialOnboardingType.TRIAL_PRINT_AND_SHARE:
      header = (
        <div className="flex flex-col gap-4">
          <div className="bg-accept-medium/30 rounded-full flex p-1 mx-auto">
            <CheckCircleIcon className="w-14 h-14 text-white" />
          </div>
          <div className="text-gray-800 text-xl font-bold">{t('limit.dialog.printAndShare.title')}</div>
          <div className="font-normal text-xs text-gray-700">{t('limit.dialog.printAndShare.description')}</div>
        </div>
      );
      description = <div className="mb-12 flex md:mb-auto"></div>;
      decorativeContent = (
        <div className="block p-8 bg-gray-100 md:bg-transparent m-4 mt-0 rounded-2xl h-72">
          {qrRendererProps && qrCode && !isLoading ? (
            <div className="md:h-52 rounded-2xl bg-[#DDE1E7] flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 justify-center items-center w-44 h-44 md:w-52 shadow-xl">
              <div className="relative w-28 md:w-36">
                <QRCodeRendererSVG
                  {...qrRendererProps}
                  patternColor="#455773"
                  cornerColor="#455773"
                  backgroundColor="#DDE1E7"
                />
                <FloatingIcons />
              </div>
            </div>
          ) : (
            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
              <ContentLoader isEmpty={false} loading entity="QR Codes" />
            </div>
          )}
        </div>
      );
      break;
    case ETrialOnboardingType.TRIAL_ONBOADRDING:
      getTrialOnboardingContentByType();
      break;

    default:
      header = <>{t('limit.dialog.common.upgradeToUnlock')}</>;
      description = <></>;
      decorativeContent = <></>;
      break;
  }

  return {
    header,
    description,
    decorativeContent,
    shouldShowAnimatedArrow,
  };
};

const icons = [ScanIcon, AttachIcon, BrowserIcon, PrinterIcon, ShareIcon].sort(() => Math.random() - 0.5);

export const FloatingIcons: React.FC = () => {
  const [animate, setAnimate] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const radiusStart = 80;
  const radiusEnd = isMobile ? 150 * 0.9 : 150;

  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      <div className="relative w-96 h-96 flex items-center justify-center">
        {icons.map((IconComponent, i) => {
          const angle = (i / icons.length) * 2 * Math.PI;
          const offsetStart = Math.random() * 20 - 10;
          const offsetEnd = Math.random() * 40 - 20;
          const x0 = Math.cos(angle) * (radiusStart + offsetStart);
          const y0 = Math.sin(angle) * (radiusStart + offsetStart);
          const x1 = Math.cos(angle + (Math.random() * 0.2 - 0.1)) * (radiusEnd + offsetEnd);
          const y1 = Math.sin(angle + (Math.random() * 0.2 - 0.1)) * (radiusEnd + offsetEnd);

          const delay = `${(Math.random() * 0.5).toFixed(2)}s`;

          const style = animate
            ? {
                transform: `translate(${x1}px, ${y1}px)`,
                opacity: 1,
                transition: `transform 1.2s ease-out ${delay}, opacity 1.2s ease-out ${delay}`,
              }
            : {
                transform: `translate(${x0}px, ${y0}px)`,
                opacity: 0,
              };

          return (
            <div
              key={i}
              style={style}
              className="absolute w-12 h-12 bg-white rounded-lg drop-shadow-lg flex items-center justify-center"
            >
              <IconComponent className="w-6 h-6 text-gray-800" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

type ConfettiProps = {
  run: boolean;
  fullscreen?: boolean;
  particleOptions?: Options;
};

export const Confetti = ({ run, fullscreen = false, particleOptions }: ConfettiProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (run && canvasRef.current) {
      const myConfetti = confetti.create(canvasRef.current, { resize: true });
      myConfetti({
        particleCount: 200,
        spread: 60,
        origin: { y: 0.5 },
        ...(particleOptions || {}),
      });
    }
  }, [run, particleOptions]);

  return (
    <canvas
      ref={canvasRef}
      className={clsxm(
        `pointer-events-none absolute top-0 left-0 w-full h-full z-10`,
        fullscreen && 'fixed top-0 left-0 w-screen h-screen'
      )}
    />
  );
};

type ArrowProps = {
  from: string;
  to: string;
  color?: string;
  curved?: boolean;
  curveX?: number;
  curveY?: number;
  doubleHead?: boolean;
  trigger?: boolean;
  offsetStartX?: number;
  offsetStartY?: number;
  offsetEndX?: number;
  offsetEndY?: number;
};

export const AnimatedArrowForOnboarding: React.FC<ArrowProps> = ({
  from,
  to,
  color = 'black',
  curved = true,
  offsetStartX = 0.5,
  offsetStartY = 0.5,
  offsetEndX = 0.5,
  offsetEndY = 0.5,
}) => {
  const idPrefix = useMemo(() => `${Math.random()}`, []);
  const [path, setPath] = useState('');
  const [svgElement, setSvgElement] = useState<SVGSVGElement | null>(null);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (!svgElement) {
      return;
    }

    const updatePath = () => {
      const fromEl = document.getElementById(from);
      const toEl = document.getElementById(to);
      if (!fromEl || !toEl) {
        return;
      }

      const svgRect = svgElement.getBoundingClientRect();
      const rect1 = fromEl.getBoundingClientRect();
      const rect2 = toEl.getBoundingClientRect();

      const startX = rect1.left + rect1.width * offsetStartX - svgRect.left;
      const startY = rect1.top + rect1.height * offsetStartY - svgRect.top;
      const endX = rect2.left + rect2.width * offsetEndX - svgRect.left;
      const endY = rect2.top + rect2.height * offsetEndY - svgRect.top;

      const radiusX = Math.abs(endX - startX);
      const radiusY = Math.abs(endY - startY);

      const d = curved
        ? `M ${startX},${startY} 
         A ${radiusX},${radiusY} 0 0,0 ${endX},${endY}`
        : `M ${startX},${startY} L ${endX},${endY}`;

      setPath(d);
    };

    updatePath();
    window.addEventListener('resize', updatePath);

    // re-calc positions again after slight delay
    const optimizeTimer = setTimeout(updatePath, 100);

    setShouldShow(true);
    return () => {
      window.removeEventListener('resize', updatePath);
      clearTimeout(optimizeTimer);
      setShouldShow(false);
    };
  }, [from, to, curved, offsetStartX, offsetStartY, offsetEndX, offsetEndY, svgElement]);

  return (
    <svg ref={setSvgElement} className="pointer-events-none absolute inset-0 w-full h-full z-0">
      <defs>
        <marker id={`${idPrefix}-arrowhead`} markerWidth="10" markerHeight="7" refX="5" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill={color} />
        </marker>
      </defs>
      <path
        d={path}
        stroke={color}
        className={clsxm(
          'fill-none stroke-2 opacity-0',
          'transition-opacity duration-1000 delay-1000',
          shouldShow && 'opacity-100'
        )}
        markerEnd={`url(#${idPrefix}-arrowhead)`}
        strokeDasharray="5"
      />
      <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="1s" repeatCount="indefinite" />
    </svg>
  );
};
